import React, { useState } from 'react';
import { Input } from '../ui/input';
import { X, MagnifyingGlass, CheckCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { backdropClick, stopBackdropPropagation } from '../../lib/modalBackdrop';

const API = process.env.REACT_APP_BACKEND_URL;

const EDITABLE_FIELDS = [
  { key: 'codice_ateco', label: 'Cod. ATECO' },
  { key: 'sede_legale_indirizzo', label: 'Sede Legale' },
  { key: 'sede_legale_comune', label: 'Comune Sede' },
  { key: 'sede_legale_prov_cap', label: 'Prov/CAP Sede' },
  { key: 'legale_rappresentante', label: 'Legale Rappr.' },
  { key: 'cf_legale_rappresentante', label: 'CF Legale Rappr.' },
  { key: 'telefono', label: 'Telefono' },
  { key: 'email', label: 'Email' },
  { key: 'codice_destinatario_pec', label: 'SDI / PEC' },
  { key: 'pdr_code', label: 'Codice PDR' },
  { key: 'fornitore_attuale', label: 'Fornitore Attuale' },
  { key: 'codice_remi', label: 'Codice REMI' },
  { key: 'nome_offerta', label: 'Nome CTE' },
  { key: 'codice_agenzia', label: 'Cod. Agenzia' },
  { key: 'codice_agente', label: 'Cod. Agente' },
  { key: 'codice_promozionale', label: 'Cod. Promo' },
  { key: 'intestatario_cc', label: 'Intestatario CC' },
  { key: 'cf_intestatario', label: 'CF Intestatario' },
  { key: 'iban', label: 'IBAN' },
  { key: 'metodo_pagamento', label: 'Pagamento' },
  { key: 'regime_iva', label: 'Regime IVA' },
  { key: 'tensione', label: 'Tensione' },
  { key: 'mercato_provenienza', label: 'Mercato' },
];

const SELECT_FIELDS = {
  metodo_pagamento: ['', 'SDD', 'Bonifico', 'Carta'],
  regime_iva: ['', 'Ordinaria', 'Agevolata', 'Esenzione'],
  tensione: ['', 'Bassa', 'Media', 'Alta'],
  mercato_provenienza: ['', 'Libero', 'Tutela'],
  tipo_fornitura: ['', 'Luce', 'Gas', 'Luce+Gas'],
  tipologia_uso: ['', 'Domestico', 'Altri Usi', 'Pubblica Amm.'],
};

export { EDITABLE_FIELDS, SELECT_FIELDS };

export default function LeadEditModal({ editData, setEditData, onClose, onSave }) {
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoStatus, setAutoStatus] = useState(null); // {type:'ok'|'warn', msg}
  const [cteOptions, setCteOptions] = useState([]); // [{id, filename, label}]

  // Load CTE catalog so the user can assign one already in the Lead phase
  // (it carries over to PreContratto when promoted, no re-selection needed).
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/documents`, { credentials: 'include' });
        if (res.ok && !cancelled) {
          const data = await res.json();
          const system = (data.system || []).filter(d => d.type === 'cte');
          const custom = (data.custom || []).filter(d => d.type === 'cte');
          setCteOptions([...system, ...custom]);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchFromOpenapi = async () => {
    const piva = (editData.partita_iva || '').trim();
    if (!piva || piva.length !== 11) {
      setAutoStatus({ type: 'warn', msg: 'P.IVA non valida (servono 11 cifre)' });
      return;
    }
    setAutoLoading(true);
    setAutoStatus(null);
    try {
      const res = await fetch(`${API}/api/leads/autocomplete-from-piva`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ piva }),
      });
      const r = await res.json();
      if (!res.ok) {
        setAutoStatus({ type: 'warn', msg: r.detail || 'Errore Openapi' });
        return;
      }
      if (!r.success) {
        setAutoStatus({ type: 'warn', msg: r.error || 'Errore' });
        return;
      }
      if (!r.found) {
        setAutoStatus({ type: 'warn', msg: r.message || 'P.IVA non trovata in Openapi' });
        return;
      }
      // Map response to lead fields
      const provCap = [r.provincia_sede, r.cap_sede].filter(Boolean).join(' / ');
      setEditData(prev => ({
        ...prev,
        ragione_sociale: r.ragione_sociale || prev.ragione_sociale,
        codice_ateco: r.ateco || prev.codice_ateco,
        sede_legale_indirizzo: r.indirizzo_sede || prev.sede_legale_indirizzo,
        sede_legale_comune: r.comune_sede || prev.sede_legale_comune,
        sede_legale_prov_cap: provCap || prev.sede_legale_prov_cap,
        codice_fiscale: r.codice_fiscale || prev.codice_fiscale,
      }));
      const inactive = r.stato_attivita && !/ATTIV/i.test(r.stato_attivita);
      if (inactive) {
        setAutoStatus({ type: 'warn', msg: `⚠️ Azienda ${r.stato_attivita} — verifica prima di procedere` });
        toast.warning(`Azienda ${r.stato_attivita}`, { description: 'Dati anagrafici recuperati ma stato non attivo.' });
      } else {
        setAutoStatus({ type: 'ok', msg: `${r.ragione_sociale} — ${r.stato_attivita || 'OK'}` });
        toast.success('Dati Openapi recuperati', { description: r.ragione_sociale });
      }
    } catch (e) {
      setAutoStatus({ type: 'warn', msg: 'Errore di rete' });
    } finally {
      setAutoLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" {...backdropClick(onClose)}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6" {...stopBackdropPropagation} data-testid="edit-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900">Modifica Lead: {editData.ragione_sociale}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        {/* Anagrafica + Autocomplete Openapi */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4" data-testid="anagrafica-openapi-block">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-1">
              <label className="text-xs text-slate-500 block mb-1">Partita IVA</label>
              <Input
                value={editData.partita_iva || ''}
                onChange={e => setEditData(p => ({ ...p, partita_iva: e.target.value.replace(/\D/g, '').slice(0, 11) }))}
                placeholder="11 cifre"
                className="text-sm font-mono"
                maxLength={11}
                data-testid="piva-input"
              />
            </div>
            <div className="md:col-span-1">
              <label className="text-xs text-slate-500 block mb-1">Ragione Sociale</label>
              <Input
                value={editData.ragione_sociale || ''}
                onChange={e => setEditData(p => ({ ...p, ragione_sociale: e.target.value }))}
                className="text-sm"
                data-testid="ragsoc-input"
              />
            </div>
            <div className="md:col-span-1">
              <button
                type="button"
                onClick={fetchFromOpenapi}
                disabled={autoLoading || !(editData.partita_iva || '').trim()}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-md text-sm font-semibold transition-colors"
                data-testid="autocomplete-openapi-btn"
                title="Recupera anagrafica da Openapi (gratis)">
                <MagnifyingGlass size={14} weight="bold" />
                {autoLoading ? 'Recupero…' : 'Recupera da Openapi'}
              </button>
            </div>
          </div>
          {autoStatus && (
            <div
              data-testid="autocomplete-status"
              className={`mt-2 flex items-start gap-2 px-3 py-2 rounded text-xs ${
                autoStatus.type === 'ok'
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                  : 'bg-amber-50 border border-amber-300 text-amber-900'
              }`}>
              {autoStatus.type === 'ok'
                ? <CheckCircle size={14} weight="bold" className="flex-shrink-0 mt-0.5" />
                : <Warning size={14} weight="bold" className="flex-shrink-0 mt-0.5" />}
              <span>{autoStatus.msg}</span>
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-2">
            🆓 Chiamata gratuita IT-start: pre-popola Ragione Sociale, sede, ATECO e codice fiscale.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {EDITABLE_FIELDS.map(f => (
            <div key={f.key}>
              <label className="text-xs text-slate-500 block mb-1">{f.label}</label>
              {SELECT_FIELDS[f.key] ? (
                <select value={editData[f.key] || ''} onChange={e => setEditData(p => ({...p, [f.key]: e.target.value}))}
                  className="w-full text-sm border rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-orange-200 focus:border-orange-400 outline-none">
                  {SELECT_FIELDS[f.key].map(o => <option key={o} value={o}>{o || '-- Seleziona --'}</option>)}
                </select>
              ) : (
                <Input value={editData[f.key] || ''} onChange={e => setEditData(p => ({...p, [f.key]: e.target.value}))}
                  className="text-sm" placeholder={f.label} />
              )}
            </div>
          ))}
          {/* CTE assignment (Sprint 7 - was only in Precontratti, now also in Lead) */}
          <div className="md:col-span-3 border-t border-slate-200 pt-3 mt-1">
            <label className="text-xs text-slate-500 block mb-1">CTE (Condizioni Tecnico-Economiche)</label>
            <select value={editData.cte_document || ''}
              onChange={e => setEditData(p => ({...p, cte_document: e.target.value}))}
              className="w-full text-sm border rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-orange-200 focus:border-orange-400 outline-none"
              data-testid="lead-cte-select">
              <option value="">-- Nessuna CTE assegnata --</option>
              {cteOptions.map(d => <option key={d.id} value={d.filename}>{d.label || d.filename}</option>)}
            </select>
            <p className="text-[11px] text-slate-500 mt-1">
              Assegna una CTE già in fase Lead: verrà ereditata automaticamente quando il lead viene promosso a precontratto.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Annulla</button>
          <button onClick={onSave} className="px-4 py-2 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-md font-medium" data-testid="save-lead-btn">
            Salva Modifiche
          </button>
        </div>
      </div>
    </div>
  );
}
